import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  getInventory,
  getInventoryItem,
  getInventorySummary,
  getInventoryTotalsBySkus,
  createInventoryItem,
  updateInventoryItem,
  receiveInventory,
  transferInventory,
  adjustInventory,
  createManualInventoryItem,
  deleteInventoryItem,
  checkExistingInventory,
  getSerializedItems,
  getSerializedItem,
  getSerializedItemsSummary,
  getSerializedItemMovements,
  createSerializedItem,
  updateSerializedItem,
  transferSerializedItem,
  changeDeviceStatus,
  getMovements,
  getMovementSummary,
  searchSkus,
} from '../api';
import type {
  InventoryFilters,
  SerializedItemFilters,
  MovementFilters,
  CreateInventoryInput,
  UpdateInventoryInput,
  ReceiveInventoryInput,
  TransferInventoryInput,
  AdjustInventoryInput,
  CreateManualInventoryInput,
  CreateSerializedItemInput,
  UpdateSerializedItemInput,
  ChangeDeviceStatusInput,
} from '../types';
import { wmsLocationKeys } from './useWmsLocations';

// Helper to extract error message
function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'object' && error !== null && 'message' in error) {
    return String((error as { message: unknown }).message);
  }
  return 'An unexpected error occurred';
}

// Query keys
export const wmsInventoryKeys = {
  all: ['wms-inventory'] as const,
  items: (filters?: InventoryFilters) => filters !== undefined
    ? [...wmsInventoryKeys.all, 'items', filters] as const
    : [...wmsInventoryKeys.all, 'items'] as const,
  item: (id: string) => [...wmsInventoryKeys.all, 'item', id] as const,
  bySku: (skuId: string) => [...wmsInventoryKeys.all, 'by-sku', skuId] as const,
  skuTotals: (ids: string[]) => [...wmsInventoryKeys.all, 'sku-totals', ids] as const,
  summary: () => [...wmsInventoryKeys.all, 'summary'] as const,
  serialized: (filters?: SerializedItemFilters) => filters !== undefined
    ? [...wmsInventoryKeys.all, 'serialized', filters] as const
    : [...wmsInventoryKeys.all, 'serialized'] as const,
  serializedItem: (id: string) => [...wmsInventoryKeys.all, 'serialized-item', id] as const,
  serializedSummary: () => [...wmsInventoryKeys.all, 'serialized-summary'] as const,
  serializedMovements: (id: string, filters?: MovementFilters) => [...wmsInventoryKeys.all, 'serialized-movements', id, filters] as const,
  movements: (filters?: MovementFilters) => filters !== undefined
    ? [...wmsInventoryKeys.all, 'movements', filters] as const
    : [...wmsInventoryKeys.all, 'movements'] as const,
  movementSummary: (days?: number) => [...wmsInventoryKeys.all, 'movement-summary', days] as const,
  skuSearch: (query: string) => [...wmsInventoryKeys.all, 'sku-search', query] as const,
  existing: (mongoSkuId: string, locationId: string) => [...wmsInventoryKeys.all, 'existing', mongoSkuId, locationId] as const,
};

// ============================================================================
// Inventory Item Hooks
// ============================================================================

export function useInventoryTotalsBySkus(mongoSkuIds: string[]) {
  return useQuery({
    queryKey: wmsInventoryKeys.skuTotals(mongoSkuIds),
    queryFn: () => getInventoryTotalsBySkus(mongoSkuIds),
    enabled: mongoSkuIds.length > 0,
    staleTime: 60_000,
  });
}

export function useInventory(filters: InventoryFilters = {}) {
  return useQuery({
    queryKey: wmsInventoryKeys.items(filters),
    queryFn: () => getInventory(filters),
  });
}

export function useInventoryItem(id: string) {
  return useQuery({
    queryKey: wmsInventoryKeys.item(id),
    queryFn: () => getInventoryItem(id),
    enabled: !!id,
  });
}

export function useInventorySummary() {
  return useQuery({
    queryKey: wmsInventoryKeys.summary(),
    queryFn: getInventorySummary,
  });
}

export function useCreateInventoryItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateInventoryInput) => createInventoryItem(data),
    onSuccess: (item) => {
      queryClient.invalidateQueries({ queryKey: wmsInventoryKeys.items() });
      queryClient.invalidateQueries({ queryKey: wmsInventoryKeys.summary() });
      toast.success(`Inventory item "${item.sku_code}" created successfully`);
    },
    onError: (error) => {
      toast.error('Failed to create inventory item', { description: getErrorMessage(error) });
    },
  });
}

export function useUpdateInventoryItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateInventoryInput }) => updateInventoryItem(id, data),
    onSuccess: (_item, variables) => {
      queryClient.invalidateQueries({ queryKey: wmsInventoryKeys.items() });
      queryClient.invalidateQueries({ queryKey: wmsInventoryKeys.item(variables.id) });
      queryClient.invalidateQueries({ queryKey: wmsInventoryKeys.summary() });
      queryClient.invalidateQueries({ queryKey: [...wmsLocationKeys.all, 'locations'] });
      queryClient.invalidateQueries({ queryKey: wmsLocationKeys.summary() });
      toast.success('Inventory item updated successfully');
    },
    onError: (error) => {
      toast.error('Failed to update inventory item', { description: getErrorMessage(error) });
    },
  });
}

// ============================================================================
// Stock Movement Hooks
// ============================================================================

export function useReceiveInventory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ReceiveInventoryInput) => receiveInventory(data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: wmsInventoryKeys.items() });
      queryClient.invalidateQueries({ queryKey: wmsInventoryKeys.summary() });
      queryClient.invalidateQueries({ queryKey: wmsInventoryKeys.movements() });
      queryClient.invalidateQueries({ queryKey: wmsLocationKeys.summary() });
      toast.success(`Received ${result.quantity} units successfully`);
    },
    onError: (error) => {
      toast.error('Failed to receive inventory', { description: getErrorMessage(error) });
    },
  });
}

export function useTransferInventory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: TransferInventoryInput) => transferInventory(data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: wmsInventoryKeys.items() });
      queryClient.invalidateQueries({ queryKey: wmsInventoryKeys.summary() });
      queryClient.invalidateQueries({ queryKey: wmsInventoryKeys.movements() });
      toast.success(`Transferred ${result.quantity} units successfully`);
    },
    onError: (error) => {
      toast.error('Failed to transfer inventory', { description: getErrorMessage(error) });
    },
  });
}

export function useAdjustInventory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: AdjustInventoryInput) => adjustInventory(data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: wmsInventoryKeys.items() });
      queryClient.invalidateQueries({ queryKey: wmsInventoryKeys.summary() });
      queryClient.invalidateQueries({ queryKey: wmsInventoryKeys.movements() });
      const change = result.difference > 0 ? `+${result.difference}` : result.difference;
      toast.success(`Stock adjusted (${change}) successfully`);
    },
    onError: (error) => {
      toast.error('Failed to adjust inventory', { description: getErrorMessage(error) });
    },
  });
}

export function useCreateManualInventoryItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateManualInventoryInput) => createManualInventoryItem(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: wmsInventoryKeys.items() });
      queryClient.invalidateQueries({ queryKey: wmsInventoryKeys.summary() });
      queryClient.invalidateQueries({ queryKey: wmsInventoryKeys.movements() });
      queryClient.invalidateQueries({ queryKey: wmsLocationKeys.summary() });
      toast.success('Item added to inventory');
    },
    onError: (error) => {
      toast.error('Failed to add item', { description: getErrorMessage(error) });
    },
  });
}

export function useDeleteInventoryItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteInventoryItem(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: wmsInventoryKeys.items() });
      queryClient.invalidateQueries({ queryKey: wmsInventoryKeys.summary() });
      toast.success('Inventory item deleted successfully');
    },
    onError: (error) => {
      toast.error('Failed to delete inventory item', { description: getErrorMessage(error) });
    },
  });
}

export function useCheckExistingInventory(mongoSkuId: string | undefined, locationId: string | undefined) {
  return useQuery({
    queryKey: wmsInventoryKeys.existing(mongoSkuId || '', locationId || ''),
    queryFn: () => checkExistingInventory(mongoSkuId!, locationId!),
    enabled: !!mongoSkuId && !!locationId,
  });
}

// ============================================================================
// Serialized Item Hooks
// ============================================================================

export function useSerializedItems(filters: SerializedItemFilters = {}) {
  return useQuery({
    queryKey: wmsInventoryKeys.serialized(filters),
    queryFn: () => getSerializedItems(filters),
  });
}

export function useSerializedItem(id: string) {
  return useQuery({
    queryKey: wmsInventoryKeys.serializedItem(id),
    queryFn: () => getSerializedItem(id),
    enabled: !!id,
  });
}

export function useCreateSerializedItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateSerializedItemInput) => createSerializedItem(data),
    onSuccess: (item) => {
      queryClient.invalidateQueries({ queryKey: wmsInventoryKeys.serialized() });
      queryClient.invalidateQueries({ queryKey: wmsInventoryKeys.items() });
      queryClient.invalidateQueries({ queryKey: wmsInventoryKeys.summary() });
      toast.success(`Serialized item "${item.serial_number}" created successfully`);
    },
    onError: (error) => {
      toast.error('Failed to create serialized item', { description: getErrorMessage(error) });
    },
  });
}

export function useTransferSerializedItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, toLocationId }: { id: string; toLocationId: string }) =>
      transferSerializedItem(id, toLocationId),
    onSuccess: (item, variables) => {
      queryClient.invalidateQueries({ queryKey: wmsInventoryKeys.serialized() });
      queryClient.invalidateQueries({ queryKey: wmsInventoryKeys.serializedItem(variables.id) });
      queryClient.invalidateQueries({ queryKey: wmsInventoryKeys.movements() });
      toast.success(`Item "${item.serial_number}" transferred successfully`);
    },
    onError: (error) => {
      toast.error('Failed to transfer item', { description: getErrorMessage(error) });
    },
  });
}

export function useUpdateSerializedItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateSerializedItemInput }) =>
      updateSerializedItem(id, data),
    onSuccess: (item, variables) => {
      queryClient.invalidateQueries({ queryKey: wmsInventoryKeys.serialized() });
      queryClient.invalidateQueries({ queryKey: wmsInventoryKeys.serializedItem(variables.id) });
      queryClient.invalidateQueries({ queryKey: wmsInventoryKeys.serializedSummary() });
      toast.success(`Item "${item.serial_number}" updated successfully`);
    },
    onError: (error) => {
      toast.error('Failed to update item', { description: getErrorMessage(error) });
    },
  });
}

export function useChangeDeviceStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ChangeDeviceStatusInput }) =>
      changeDeviceStatus(id, data),
    onSuccess: (result, variables) => {
      queryClient.invalidateQueries({ queryKey: wmsInventoryKeys.serialized() });
      queryClient.invalidateQueries({ queryKey: wmsInventoryKeys.serializedItem(variables.id) });
      queryClient.invalidateQueries({ queryKey: wmsInventoryKeys.serializedSummary() });
      queryClient.invalidateQueries({ queryKey: wmsInventoryKeys.serializedMovements(variables.id) });
      queryClient.invalidateQueries({ queryKey: wmsInventoryKeys.movements() });
      toast.success(`Device status changed to "${result.status_change.new_status}"`);
    },
    onError: (error) => {
      toast.error('Failed to change device status', { description: getErrorMessage(error) });
    },
  });
}

export function useSerializedItemsSummary() {
  return useQuery({
    queryKey: wmsInventoryKeys.serializedSummary(),
    queryFn: getSerializedItemsSummary,
  });
}

export function useSerializedItemMovements(id: string, filters: MovementFilters = {}) {
  return useQuery({
    queryKey: wmsInventoryKeys.serializedMovements(id, filters),
    queryFn: () => getSerializedItemMovements(id, filters),
    enabled: !!id,
  });
}

// ============================================================================
// Movement Hooks
// ============================================================================

export function useMovements(filters: MovementFilters = {}) {
  return useQuery({
    queryKey: wmsInventoryKeys.movements(filters),
    queryFn: () => getMovements(filters),
  });
}

export function useMovementSummary(days: number = 30) {
  const toDate = new Date().toISOString().split('T')[0];
  const fromDate = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

  return useQuery({
    queryKey: wmsInventoryKeys.movementSummary(days),
    queryFn: () => getMovementSummary({ fromDate, toDate }),
  });
}

// ============================================================================
// SKU Search Hook
// ============================================================================

export function useSkuSearch(query: string) {
  return useQuery({
    queryKey: wmsInventoryKeys.skuSearch(query),
    queryFn: () => searchSkus(query),
    enabled: query.length >= 2,
    staleTime: 30000, // Cache for 30 seconds
  });
}
